<div class="reset-password-page">
  <!-- Arrière-plan animé -->
  <div class="animated-background">
    <!-- Cercles décoratifs animés -->
    <div class="decorative-circles">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
      <div class="circle circle-4"></div>
      <div class="circle circle-5"></div>
    </div>

    <!-- Particules lumineuses -->
    <div class="light-particles">
      <div class="light-particle particle-1"></div>
      <div class="light-particle particle-2"></div>
      <div class="light-particle particle-3"></div>
      <div class="light-particle particle-4"></div>
      <div class="light-particle particle-5"></div>
      <div class="light-particle particle-6"></div>
      <div class="light-particle particle-7"></div>
      <div class="light-particle particle-8"></div>
    </div>

    <!-- Icônes flottantes -->
    <div class="floating-icons">
      <i class="bi bi-shield-lock-fill icon-1"></i>
      <i class="bi bi-key-fill icon-2"></i>
      <i class="bi bi-lock-fill icon-3"></i>
      <i class="bi bi-shield-check icon-4"></i>
    </div>
  </div>

  <div class="container reset-password-container">
    <div class="row justify-content-center align-items-center min-vh-100">
      <div class="col-lg-6 col-md-8 col-sm-10">

        <!-- État : Vérification du token -->
        <div class="reset-password-card card-custom fade-in-up text-center" *ngIf="isCheckingToken">
          <div class="spinner-wrapper mb-4">
            <div class="spinner-border text-primary-color" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>
          <h2 class="mb-2">Vérification en cours...</h2>
          <p class="text-secondary">Validation de votre lien de réinitialisation</p>
        </div>

        <!-- État : Token invalide ou expiré -->
        <div class="reset-password-card card-custom fade-in-up text-center" *ngIf="!isCheckingToken && !isValidToken">
          <div class="error-icon-wrapper mb-4">
            <i class="bi bi-exclamation-triangle-fill"></i>
          </div>
          <h1 class="mb-3">Lien invalide ou expiré</h1>
          <p class="text-secondary mb-4">
            Le lien de réinitialisation que vous avez utilisé est invalide ou a expiré.
            Les liens de réinitialisation sont valables pendant 24 heures seulement.
          </p>

          <div class="alert alert-warning" role="alert">
            <i class="bi bi-info-circle me-2"></i>
            Pour des raisons de sécurité, chaque lien ne peut être utilisé qu'une seule fois.
          </div>

          <div class="action-buttons mt-4">
            <button class="btn btn-primary-custom me-2" (click)="requestNewLink()">
              <i class="bi bi-arrow-clockwise me-2"></i>
              Demander un nouveau lien
            </button>
            <button class="btn btn-outline-custom" (click)="navigateToSignin()">
              <i class="bi bi-arrow-left me-2"></i>
              Retour à la connexion
            </button>
          </div>
        </div>

        <!-- État : Formulaire de réinitialisation -->
        <div class="reset-password-card card-custom fade-in-up" *ngIf="!isCheckingToken && isValidToken && !resetSuccess">
          <div class="text-center mb-4">
            <div class="icon-wrapper mb-3">
              <i class="bi bi-shield-lock-fill"></i>
            </div>
            <h1 class="mb-2">Nouveau mot de passe</h1>
            <p class="text-secondary">
              Créez un mot de passe fort et sécurisé pour protéger votre compte
            </p>
          </div>

          <!-- Message d'erreur global -->
          <div class="alert alert-danger alert-dismissible fade show" role="alert" *ngIf="errorMessage">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            {{ errorMessage }}
            <button type="button" class="btn-close" (click)="errorMessage = ''"></button>
          </div>

          <form [formGroup]="resetPasswordForm" (ngSubmit)="onSubmit()">
            <!-- Nouveau mot de passe -->
            <div class="form-group mb-3">
              <label for="password" class="form-label">
                Nouveau mot de passe <span class="text-danger">*</span>
              </label>
              <div class="input-wrapper">
                <i class="input-icon bi bi-lock"></i>
                <input
                  [type]="showPassword ? 'text' : 'password'"
                  id="password"
                  class="form-control custom-input"
                  placeholder="Entrez votre nouveau mot de passe"
                  formControlName="password"
                  [class.is-invalid]="isFieldInvalid('password')"
                />
                <button
                  type="button"
                  class="password-toggle"
                  (click)="togglePasswordVisibility()"
                  tabindex="-1"
                >
                  <i [class]="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
              </div>

              <!-- Indicateur de force du mot de passe -->
              <div class="password-strength mt-2" *ngIf="resetPasswordForm.get('password')?.value">
                <div class="strength-bar">
                  <div
                    class="strength-fill"
                    [style.width.%]="passwordStrength"
                    [class]="getPasswordStrengthClass()"
                  ></div>
                </div>
                <small class="strength-label" [class]="getPasswordStrengthClass()">
                  {{ getPasswordStrengthLabel() }}
                </small>
              </div>

              <div class="invalid-feedback" *ngIf="isFieldInvalid('password')">
                {{ getFieldError('password') }}
              </div>
            </div>

            <!-- Exigences du mot de passe -->
            <div class="password-requirements mb-4" *ngIf="resetPasswordForm.get('password')?.value">
              <p class="requirements-title mb-2">
                <i class="bi bi-info-circle me-2"></i>
                Votre mot de passe doit contenir :
              </p>
              <div class="requirements-list">
                <div
                  class="requirement-item"
                  *ngFor="let req of passwordRequirements"
                  [class.met]="req.met"
                >
                  <i [class]="req.met ? 'bi bi-check-circle-fill' : 'bi bi-circle'"></i>
                  <span>{{ req.label }}</span>
                </div>
              </div>
            </div>

            <!-- Confirmer mot de passe -->
            <div class="form-group mb-4">
              <label for="confirmPassword" class="form-label">
                Confirmer le mot de passe <span class="text-danger">*</span>
              </label>
              <div class="input-wrapper">
                <i class="input-icon bi bi-lock-fill"></i>
                <input
                  [type]="showConfirmPassword ? 'text' : 'password'"
                  id="confirmPassword"
                  class="form-control custom-input"
                  placeholder="Confirmez votre mot de passe"
                  formControlName="confirmPassword"
                  [class.is-invalid]="isFieldInvalid('confirmPassword') || resetPasswordForm.hasError('passwordMismatch')"
                />
                <button
                  type="button"
                  class="password-toggle"
                  (click)="toggleConfirmPasswordVisibility()"
                  tabindex="-1"
                >
                  <i [class]="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
                <div class="invalid-feedback" *ngIf="isFieldInvalid('confirmPassword') || resetPasswordForm.hasError('passwordMismatch')">
                  {{ getFieldError('confirmPassword') }}
                </div>
              </div>
            </div>

            <!-- Bouton de soumission -->
            <button
              type="submit"
              class="btn btn-primary-custom w-100 mb-3"
              [disabled]="isLoading || !allRequirementsMet"
            >
              <span *ngIf="!isLoading">
                <i class="bi bi-check-circle me-2"></i>
                Réinitialiser le mot de passe
              </span>
              <span *ngIf="isLoading" class="d-flex align-items-center justify-content-center">
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Réinitialisation en cours...
              </span>
            </button>

            <!-- Info sécurité -->
            <div class="security-note text-center">
              <small class="text-secondary">
                <i class="bi bi-shield-check text-primary-color me-1"></i>
                Votre mot de passe est chiffré et sécurisé
              </small>
            </div>
          </form>
        </div>

        <!-- État : Réinitialisation réussie -->
        <div class="reset-password-card card-custom fade-in-up text-center" *ngIf="resetSuccess">
          <div class="success-icon-wrapper mb-4">
            <i class="bi bi-check-circle-fill"></i>
          </div>
          <h1 class="mb-3">Mot de passe réinitialisé !</h1>
          <p class="text-secondary mb-4">
            Votre mot de passe a été réinitialisé avec succès.
            Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
          </p>

          <!-- Animation de succès -->
          <div class="success-animation mb-4">
            <div class="checkmark-circle">
              <div class="checkmark"></div>
            </div>
          </div>

          <!-- Message de redirection -->
          <div class="redirect-info mb-4">
            <div class="spinner-border spinner-border-sm text-primary-color me-2" role="status">
              <span class="visually-hidden">Redirection...</span>
            </div>
            <small class="text-secondary">Redirection vers la page de connexion...</small>
          </div>

          <!-- Bouton manuel -->
          <button class="btn btn-primary-custom" (click)="navigateToSignin()">
            <i class="bi bi-box-arrow-in-right me-2"></i>
            Se connecter maintenant
          </button>
        </div>

        <!-- Section aide -->
        <div class="help-section text-center mt-4 fade-in" *ngIf="!isCheckingToken">
          <p class="text-secondary small mb-2">
            <i class="bi bi-question-circle me-1"></i>
            Besoin d'aide ?
          </p>
          <div class="help-links">
            <a routerLink="/contact" class="text-primary-color me-3">Support</a>
            <a routerLink="/faqs" class="text-primary-color me-3">FAQ</a>
            <a routerLink="/contact" class="text-primary-color">Nous contacter</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
